import { ref } from 'vue';
import type { AttachmentFile } from '@/components/shared/documents';

const formatFileSize = (size: number) => {
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.round(size / 1024)} KB`;
};

export const createPreviewUrl = (file: File) => URL.createObjectURL(file);

export function useEntityAttachments() {
  const pendingFilesByIndex = ref<Record<number, AttachmentFile[]>>({});
  const deletedFileIds = ref<number[]>([]);

  const getExistingFiles = (index: number, existingFiles: AttachmentFile[] = []) =>
    existingFiles.filter(file => !deletedFileIds.value.includes(Number(file.id)));

  const getPendingFiles = (index: number) => pendingFilesByIndex.value[index] || [];

  const addFiles = (files: File[], index: number) => {
    const current = pendingFilesByIndex.value[index] || [];
    const next = [...current];

    for (const file of files) {
      next.push({
        id: `pending-${index}-${Date.now()}-${Math.random()}`,
        file_name: file.name,
        file_size: formatFileSize(file.size),
        previewUrl: createPreviewUrl(file),
        isPending: true,
        pendingFile: file,
      });
    }

    pendingFilesByIndex.value[index] = next;
  };

  const removeFile = (file: AttachmentFile) => {
    if (file.isPending) {
      Object.keys(pendingFilesByIndex.value).forEach(key => {
        const index = Number(key);
        pendingFilesByIndex.value[index] = pendingFilesByIndex.value[index].filter(
          item => item.id !== file.id
        );
      });
      return;
    }

    if (file.id) {
      deletedFileIds.value.push(Number(file.id));
    }
  };

  const resetAttachments = () => {
    pendingFilesByIndex.value = {};
    deletedFileIds.value = [];
  };

  const getPendingUploadsForIndex = (index: number) =>
    getPendingFiles(index)
      .filter(file => file.pendingFile)
      .map(file => ({
        file: file.pendingFile as File,
        file_name: file.file_name || file.pendingFile?.name,
        file_size: file.file_size,
      }));

  return {
    pendingFilesByIndex,
    deletedFileIds,
    getExistingFiles,
    getPendingFiles,
    addFiles,
    removeFile,
    resetAttachments,
    getPendingUploadsForIndex,
  };
}
